import { useMemo } from "react";
import type { ViewTableRowDragOut } from "@design-system/ViewTable/ViewTable";
import type { DraftRefsTarget } from "../useDraftRefsByTarget";
import type { ChatDragItem } from "./chatDrag.types";
import { useChatDragAvailable } from "./chatDragContext";
import { resolveRowDragItems } from "./resolveRowDragItems";

interface UseRowChatDragOptions<TRow, TItem> {
    /** Singular noun for the preview chip, e.g. "transaction". */
    noun: string;
    canAddToChat: boolean;
    /** The chat item a row stands for, or null for rows that can't be added (they don't drag). */
    getRowItem: (row: TRow) => TItem | null;
    selectedItems: TItem[];
    /** Select-all-matching is on, so `selectedItems` isn't the full selection. */
    isSelectAll?: boolean;
    addToChat: (items: TItem[], target: DraftRefsTarget) => void;
    clearSelection: () => void;
}

/**
 * Builds a ViewTable `rowDragOut` that drags a row, or the selection it
 * belongs to, into the task chat. Undefined outside a task chat, so rows
 * don't drag there. Pass memoized callbacks.
 */
export function useRowChatDrag<TRow, TItem>({
    noun,
    canAddToChat,
    getRowItem,
    selectedItems,
    isSelectAll = false,
    addToChat,
    clearSelection,
}: UseRowChatDragOptions<TRow, TItem>): ViewTableRowDragOut<TRow> | undefined {
    const isAvailable = useChatDragAvailable();

    return useMemo(() => {
        if (!isAvailable || !canAddToChat) {
            return undefined;
        }
        return {
            getRowDragData: (row, { isSelected }): ChatDragItem | null => {
                const item = getRowItem(row);
                if (item === null) {
                    return null;
                }
                const items = resolveRowDragItems({
                    item,
                    isSelected,
                    selectedItems,
                    isSelectAll,
                });
                if (items.length === 0) {
                    return null;
                }
                return {
                    noun,
                    count: items.length,
                    add: (target) => addToChat(items, target),
                    onAdded: isSelected ? clearSelection : undefined,
                };
            },
        };
    }, [
        isAvailable,
        canAddToChat,
        getRowItem,
        selectedItems,
        isSelectAll,
        noun,
        addToChat,
        clearSelection,
    ]);
}
